import { isNil } from 'lodash';
import { Vector3 } from 'three';
import { StrikeZoneType } from '../enums/machine.enums';
import { MAX_HIT_DISTANCE_FT } from '../enums/pitches.enums';
import {
  ICSVRow,
  ICustomBreaks,
  ICustomSpins,
  IHawkeye,
  IPlateSpec,
  IStatCast,
} from '../interfaces/csv';
import {
  ITrackingData,
  ITrajektData,
} from '../interfaces/csv/exports/i-session-fires';
import {
  IFireEvent,
  IMSTargetEventDataExt,
} from '../interfaces/i-session-event';
import { DEFAULT_PLATE, IPlateLoc, ITrajectory } from '../interfaces/pitches';
import {
  IOrientation,
  ISeamOrientation,
  ISpin,
} from '../interfaces/pitches/i-base';
import { ITrackmanHit, ITrackmanPitch } from '../interfaces/trackman/i-ball';
import { IMSBSShot } from '../interfaces/training/i-msbs-shot';
import { IRapsodoShot } from '../interfaces/training/i-rapsodo-shot';
import { BallHelper } from './ball.helper';
import { HawkeyeHelper } from './hawkeye.helper';
import {
  FTPS_TO_MPH,
  FT_TO_INCHES,
  METERS_TO_FT,
  METERS_TO_INCHES,
  MPS_TO_MPH,
  RAD_TO_DEG,
  convertUnits,
  getLocalizeMatrix,
  isNumber,
  safeNumber,
} from './math.utilities';
import { PlateHelper, VISIBLE_STRIKEZONE } from './plate.helper';
import { RapsodoHelper } from './rapsodo.helper';
import { TrackmanHelper } from './trackman.helper';
import { TrajHelper } from './trajectory.helper';
import { VisionHelper } from './vision.helper';

const MOUND_DISTANCE_FT = 60.5;

const LIMITS = {
  PLATE: {
    HEIGHT: {
      MAX: 6,
      MIN: 0,
    },
    SIDE: {
      MAX: 2.5,
      MIN: -2.5,
    },
  },
};

const CSV_NUM_COL = [
  'SeamLat',
  'SeamLon',

  'seam_latitude',
  'seam_longitude',

  'PlateLocHeight',
  'PlateLocSide',

  /** allow any format to use hawkeye rotation matrix notation */
  'Xx',
  'Xy',
  'Xz',
  'Yx',
  'Yy',
  'Yz',
  'Zx',
  'Zy',
  'Zz',

  /** allow any format to specify breaks */
  'HorizontalBreakIn',
  'VerticalBreakIn',
];

export const CUSTOM_RELEASE_COL = [
  'ReleaseX',
  'ReleaseY',
  'ReleaseZ',
  'ReleaseV',
];

export const CUSTOM_BREAKS_NUM_COL = [
  ...CSV_NUM_COL,
  ...CUSTOM_RELEASE_COL,
  'HorizontalBreakIn',
  'VerticalBreakIn',
  'NetSpin',
];

export const CUSTOM_SPINS_NUM_COL = [
  ...CSV_NUM_COL,
  ...CUSTOM_RELEASE_COL,
  'SpinX',
  'SpinY',
  'SpinZ',
];

export const HAWKEYE_RELEASE_COL = [
  'releaseX',
  'releaseY',
  'releaseZ',
  'releaseSpeed',
];

export const HAWKEYE_NUM_COL = [
  ...CSV_NUM_COL,
  ...HAWKEYE_RELEASE_COL,
  'spinX',
  'spinY',
  'spinZ',
  'x0',
  'y0',
  'z0',
  'vx0',
  'vy0',
  'vz0',
  'ax0',
  'ay0',
  'az0',
];

export const STATCAST_RELEASE_COL = [
  'release_pos_x',
  'release_pos_y',
  'release_pos_z',
  'release_spin_rate',
];

export const STATCAST_NUM_COL = [
  ...CSV_NUM_COL,
  ...STATCAST_RELEASE_COL,
  'vx0',
  'vy0',
  'vz0',
  'ax',
  'ay',
  'az',
];

export class CSVHelper {
  static createOutput(row: ICSVRow, numericCols: string[]): any | undefined {
    try {
      const output: any = { ...row };
      const warnedColumns: string[] = [];
      Object.keys(row)
        .filter((key) => numericCols.includes(key) && output[key] !== undefined)
        .forEach((key) => {
          const maybeNum = safeNumber(output[key] as string);

          if (maybeNum !== undefined) {
            output[key] = maybeNum;
            return;
          }

          if (!warnedColumns.includes(key)) {
            console.warn(`Numeric column ${key} has NaN value, skipping...`);
            warnedColumns.push(key);
            return;
          }

          // already warned about this key, do nothing
        });

      return output;
    } catch (e) {
      console.error(e);
      return undefined;
    }
  }

  /** if IPlateSpec is provided via CSV, check to make sure values are usable
   * - if no IPlateSpec values are provided, return default plate
   * - if bad IPlateSpec values are provided, return an error message string
   * - if acceptable IPlateSpec values are provided, return a corresponding IPlateLoc (1:1 mapping)
   */
  static getPlateLoc(data: Partial<IPlateSpec>): IPlateLoc {
    try {
      if (data.PlateLocSide === undefined || isNaN(data.PlateLocSide)) {
        throw new Error('Plate location side is not a number.');
      }

      if (data.PlateLocHeight === undefined || isNaN(data.PlateLocHeight)) {
        throw new Error('Plate location height is not a number.');
      }

      if (data.PlateLocSide > LIMITS.PLATE.SIDE.MAX) {
        throw new Error(
          `Plate location side value (${data.PlateLocSide} ft.) is greater than the maximum value (${LIMITS.PLATE.SIDE.MAX} ft.).`
        );
      }

      if (data.PlateLocSide < LIMITS.PLATE.SIDE.MIN) {
        throw new Error(
          `Plate location side value (${data.PlateLocSide} ft.) is less than the minimum value (${LIMITS.PLATE.SIDE.MIN} ft.).`
        );
      }

      if (data.PlateLocHeight > LIMITS.PLATE.HEIGHT.MAX) {
        throw new Error(
          `Plate location height value (${data.PlateLocHeight} ft.) is greater than the maximum value (${LIMITS.PLATE.HEIGHT.MAX} ft.).`
        );
      }

      if (data.PlateLocHeight < LIMITS.PLATE.HEIGHT.MIN) {
        throw new Error(
          `Plate location height value (${data.PlateLocHeight} ft.) is less than the minimum value (${LIMITS.PLATE.HEIGHT.MIN} ft.).`
        );
      }

      return {
        plate_x: data.PlateLocSide,
        plate_z: data.PlateLocHeight,
      };
    } catch (e) {
      console.error(e);
      return DEFAULT_PLATE;
    }
  }

  static createCustomBreaks(row: ICSVRow): ICustomBreaks | undefined {
    return CSVHelper.createOutput(row, CUSTOM_BREAKS_NUM_COL);
  }

  static createCustomSpins(row: ICSVRow): ICustomSpins | undefined {
    return CSVHelper.createOutput(row, CUSTOM_SPINS_NUM_COL);
  }

  static createHawkeye(row: ICSVRow): IHawkeye | undefined {
    return CSVHelper.createOutput(row, HAWKEYE_NUM_COL);
  }

  static createStatCast(row: ICSVRow): IStatCast | undefined {
    return CSVHelper.createOutput(row, STATCAST_NUM_COL);
  }

  static hasBreakColumns(data: Partial<ICSVRow>): boolean {
    const keys = Object.keys(data);
    return ['HorizontalBreakIn', 'VerticalBreakIn'].every((m) =>
      keys.includes(m)
    );
  }

  static hasBreakDetails(data: Partial<ICSVRow>): boolean {
    return [data.HorizontalBreakIn, data.VerticalBreakIn].every((m) =>
      isNumber(m)
    );
  }

  /** same criteria as getQuaternion to detect if seam info is provided */
  static hasSeamDetails(data: Partial<ICSVRow>): boolean {
    if ([data.qw, data.qx, data.qy, data.qz].every((m) => isNumber(m))) {
      return true;
    }

    if (
      [
        data.Xx,
        data.Xy,
        data.Xz,
        data.Yx,
        data.Yy,
        data.Yz,
        data.Zx,
        data.Zy,
        data.Zz,
      ].every((m) => isNumber(m))
    ) {
      return true;
    }

    if ([data.SeamLat, data.SeamLon].every((m) => isNumber(m))) {
      return true;
    }

    if (data.SeamOrientation !== undefined) {
      return true;
    }

    return false;
  }

  static getQuatFromData(data: Partial<ICSVRow>, spin: ISpin): IOrientation {
    if ([data.qw, data.qx, data.qy, data.qz].every((m) => isNumber(m))) {
      return data as IOrientation;
    }

    const heSeams = this.getSeamsFromHawkeye(data as IHawkeye);

    if (heSeams) {
      return BallHelper.getQuatFromSeams({
        latitude_deg: heSeams.latitude_deg,
        longitude_deg: heSeams.longitude_deg,
        spin: spin,
      });
    }

    if ([data.SeamLat, data.SeamLon].every((m) => isNumber(m))) {
      return BallHelper.getQuatFromSeams({
        latitude_deg: data.SeamLat as number,
        longitude_deg: data.SeamLon as number,
        spin: spin,
      });
    }

    if (data.SeamOrientation !== undefined) {
      if (data.SeamOrientation === '2S') {
        return BallHelper.getQuatFromSeams({
          longitude_deg: 90,
          latitude_deg: 0,
          spin: spin,
        });
      }
    }

    /** default if all the above was passed over */
    return BallHelper.getQuatFromSeams({
      longitude_deg: 0,
      latitude_deg: 0,
      spin: spin,
    });
  }

  static getSeams(
    data: ICustomSpins | ICustomBreaks | IHawkeye | IStatCast
  ): ISeamOrientation | undefined {
    try {
      if (
        data.seam_latitude !== undefined &&
        data.seam_longitude !== undefined
      ) {
        return {
          latitude_deg: data.seam_latitude,
          longitude_deg: data.seam_longitude,
        };
      }

      if (data.SeamLat !== undefined && data.SeamLon !== undefined) {
        return {
          latitude_deg: data.SeamLat,
          longitude_deg: data.SeamLon,
        };
      }

      const heSeams = this.getSeamsFromHawkeye(data);

      if (heSeams) {
        return heSeams;
      }

      if (data.seamOrientation === '4S' || data.SeamOrientation === '4S') {
        return {
          latitude_deg: 0,
          longitude_deg: 0,
        };
      }

      if (data.seamOrientation === '2S' || data.SeamOrientation === '2S') {
        return {
          latitude_deg: 90,
          longitude_deg: 0,
        };
      }

      throw Error('Failed to find seams from rawData, returning undefined');
    } catch (e) {
      console.error(e);
      return undefined;
    }
  }

  private static getSeamsFromHawkeye(
    data: ICustomSpins | ICustomBreaks | IHawkeye | IStatCast
  ): ISeamOrientation | undefined {
    try {
      const spin = this.getSpin(data);
      if (!spin) {
        throw Error('Failed to get spin');
      }

      const spinAxis = BallHelper.safeNormalize(
        new Vector3(spin.wx, spin.wy, spin.wz)
      );

      const heMatrix = HawkeyeHelper.getMatrix(data);
      if (!heMatrix) {
        throw Error('Failed to get Hawkeye rotation matrix');
      }

      return HawkeyeHelper.getSeamsFromMatrix(heMatrix, spinAxis);
    } catch (e) {
      console.error(e);
      return undefined;
    }
  }

  static getSpin(
    data: ICustomSpins | ICustomBreaks | IHawkeye | IStatCast
  ): ISpin | undefined {
    try {
      const customData = data as ICustomSpins;

      const hasCustomSpin = [
        customData.SpinX,
        customData.SpinY,
        customData.SpinZ,
      ].every((m) => isNumber(m));

      if (hasCustomSpin) {
        return {
          wx: customData.SpinX,
          wy: customData.SpinY,
          wz: customData.SpinZ,
        };
      }

      const hawkeyeData = data as IHawkeye;

      const hasHawkeyeSpin = [
        hawkeyeData.spinX,
        hawkeyeData.spinY,
        hawkeyeData.spinZ,
      ].every((m) => isNumber(m));

      if (hasHawkeyeSpin) {
        return {
          wx: hawkeyeData.spinX,
          wy: hawkeyeData.spinY,
          wz: hawkeyeData.spinZ,
        };
      }

      throw Error('Failed to get spin');
    } catch (e) {
      console.error(e);
      return undefined;
    }
  }

  static mstargetToTrajekt(targetData: IMSTargetEventDataExt) {
    const seams = BallHelper.getSeamsFromBS(targetData.bs);

    const output: Partial<ITrajektData> = {
      BallType: targetData.ms?.ball_type,
      BuildPriority: targetData.build_priority,

      InGame: 'N',
      Training: 'N',
      Valid: 'Y', // will be overwritten later as per fire event
      ErrorMsg: '', // will be overwritten later as per fire event

      PitchList: targetData.list,
      PitchTitle: targetData.pitch_title,
      PitchType: targetData.pitch_type,
      PitcherFullName: targetData.pitcher,
      PitcherCustomID: targetData.pitcherCustomID ?? undefined,
      PitcherPlayerPk: targetData.pitcherPk,
      VideoTitle: targetData.video_title,

      /** pitch and video details from target */
      ReleaseX: targetData.bs?.px,
      ReleaseY: targetData.bs?.py,
      ReleaseZ: targetData.bs?.pz,
      ReleaseV: targetData.bs ? -targetData.bs?.vy : undefined,

      SpinX: targetData.bs?.wx,
      SpinY: targetData.bs?.wy,
      SpinZ: targetData.bs?.wz,

      SeamLat: seams?.latitude_deg,
      SeamLon: seams?.longitude_deg,

      TargetPlateX: targetData.plate?.plate_x,
      TargetPlateZ: targetData.plate?.plate_z,
    };

    return output;
  }

  static rapsodoToTracking(data: IRapsodoShot, msbs: IMSBSShot) {
    const spin3D = RapsodoHelper.get3DSpin(data);
    const traj = RapsodoHelper.getTrajectory(msbs.MS, data);
    const breaks = TrajHelper.getBreaks(traj);
    const plateFt = TrajHelper.getPlateLoc(traj);
    const output: ITrackingData = {
      Validated: [
        data.PITCH_StrikePositionSideConfidence,
        data.PITCH_StrikePositionHeightConfidence,
        data.PITCH_SpinConfidence,
      ].every((v) => v > 0.9)
        ? 'Y'
        : 'N',

      PITCH_GyroDegree: data.PITCH_GyroDegree,
      PITCH_HBSpin: convertUnits(METERS_TO_INCHES, data.PITCH_HBSpin) ?? null,
      PITCH_HBTrajectory: -1 * breaks.xInches,
      PITCH_HorizontalAngle: data.PITCH_HorizontalAngle,
      PITCH_PlayerID: data.PITCH_PlayerID,
      PITCH_ReleaseExtension:
        convertUnits(METERS_TO_FT, data.PITCH_ReleaseExtension) ?? null,
      PITCH_ReleaseHeight:
        convertUnits(METERS_TO_FT, data.PITCH_ReleaseHeight) ?? null,
      PITCH_ReleaseSide:
        convertUnits(METERS_TO_FT, data.PITCH_ReleaseSide) ?? null,
      PITCH_ReleaseTime: data.PITCH_ReleaseTime,
      PITCH_Speed: convertUnits(MPS_TO_MPH, data.PITCH_Speed) ?? null,
      PITCH_SpinAxis:
        BallHelper.convertTimeToDegrees(data.PITCH_SpinAxis) ?? null,
      PITCH_SpinX: spin3D?.wx ?? null,
      PITCH_SpinY: spin3D?.wy ?? null,
      PITCH_SpinZ: spin3D?.wz ?? null,
      PITCH_SpinEfficiency: data.PITCH_SpinEfficiency,
      PITCH_Strike: data.PITCH_Strike,
      PITCH_StrikeZoneHeight:
        convertUnits(FT_TO_INCHES, plateFt.plate_z) ?? null,
      PITCH_StrikeZoneSide:
        convertUnits(FT_TO_INCHES, -1 * plateFt.plate_x) ?? null,
      PITCH_StrikeZoneTime: data.PITCH_StrikeZoneTime,
      PITCH_TotalSpin: data.PITCH_TotalSpin,
      PITCH_TrueSpin: data.PITCH_TrueSpin,
      PITCH_VBSpin: convertUnits(METERS_TO_INCHES, data.PITCH_VBSpin) ?? null,
      PITCH_VBTrajectory: breaks.zInches,
      PITCH_VerticalAngle: data.PITCH_VerticalAngle,
      PITCH_ZoneTime: data.PITCH_ZoneTime,

      HIT_Distance: convertUnits(METERS_TO_FT, data.HIT_Distance) ?? null,
      HIT_LaunchAngle: data.HIT_LaunchAngle ?? null,
      HIT_ExitSpeed: convertUnits(MPS_TO_MPH, data.HIT_ExitSpeed) ?? null,
    };

    const invalidHit =
      isNil(output.HIT_Distance) || output.HIT_Distance > MAX_HIT_DISTANCE_FT;

    if (invalidHit) {
      output.HIT_Distance = null;
      output.HIT_LaunchAngle = null;
      output.HIT_ExitSpeed = null;
    }

    return output;
  }

  // todo: double-check which of the undefined items can actually be defined from trackman data
  static trackmanToTracking(config: {
    pitch: ITrackmanPitch | undefined;
    hit: ITrackmanHit | undefined;
    msbs: IMSBSShot;
  }): ITrackingData {
    const pRelease = config.pitch?.Release;
    const pLocation = config.pitch?.Location;

    const spin3D = pRelease ? TrackmanHelper.get3DSpin(pRelease) : undefined;

    const py = config.msbs.MS.py;
    const traj = config.pitch
      ? TrackmanHelper.getExtrapolatedTraj(config.pitch, py)
      : undefined;
    const breaks = traj ? TrajHelper.getBreaks(traj) : undefined;
    const plateFt = traj ? TrajHelper.getPlateLoc(traj) : undefined;

    const output: ITrackingData = {
      // todo: replace this with real logic
      Validated: (pRelease?.Speed ?? -1 > 0) ? 'Y' : 'N',

      PITCH_GyroDegree: pRelease?.SpinAxis3dLongitudinalAngle ?? null,
      PITCH_HBSpin: null, // convertUnits(METERS_TO_INCHES, data.PITCH_HBSpin),
      PITCH_HBTrajectory: breaks ? -1 * breaks.xInches : null,
      PITCH_HorizontalAngle: null, // data.PITCH_HorizontalAngle,
      PITCH_PlayerID: null, // data.PITCH_PlayerID,
      PITCH_ReleaseExtension: pRelease?.Extension ?? null,
      PITCH_ReleaseHeight: pRelease?.Height ?? null,
      PITCH_ReleaseSide: pRelease?.Side ?? null,
      PITCH_ReleaseTime: null, // data.PITCH_ReleaseTime,
      PITCH_Speed: pRelease?.Speed ?? null,
      PITCH_SpinAxis: pRelease?.SpinAxis3dTransverseAngle ?? null, // data.PITCH_SpinAxis,
      PITCH_SpinX: spin3D?.wx ?? null,
      PITCH_SpinY: spin3D?.wy ?? null,
      PITCH_SpinZ: spin3D?.wz ?? null,
      PITCH_SpinEfficiency: pRelease?.SpinAxis3dSpinEfficiency ?? null,
      PITCH_Strike: null, // data.PITCH_Strike,
      PITCH_StrikeZoneHeight:
        convertUnits(FT_TO_INCHES, plateFt?.plate_z) ?? null,
      PITCH_StrikeZoneSide: plateFt
        ? (convertUnits(FT_TO_INCHES, -1 * plateFt?.plate_x) ?? null)
        : null,
      PITCH_StrikeZoneTime: pLocation?.Time ?? null,
      PITCH_TotalSpin: null, // data.PITCH_TotalSpin,
      PITCH_TrueSpin: null, // data.PITCH_TrueSpin,
      PITCH_VBSpin: null, // convertUnits(METERS_TO_INCHES, data.PITCH_VBSpin),
      PITCH_VBTrajectory: breaks?.zInches ?? null,
      PITCH_VerticalAngle: pRelease?.VerticalAngle ?? null,
      PITCH_ZoneTime: null, // data.PITCH_ZoneTime,

      HIT_Distance: config.hit?.LandingFlat.Distance ?? null,
      HIT_LaunchAngle: config.hit?.Launch.VerticalAngle ?? null,
      HIT_ExitSpeed: config.hit?.Launch.Speed ?? null,
    };

    return output;
  }

  static visionToTracking(
    fire: IFireEvent,
    msbs: IMSBSShot,
    szType: StrikeZoneType
  ): Partial<ITrackingData> {
    const precursor = VisionHelper.getPrecursor(fire, msbs);

    const plateFt = TrajHelper.getPlateLoc(precursor.traj);
    const breaks = TrajHelper.getBreaks(precursor.traj);
    const flightTime = TrajHelper.getFlightTimeSec(precursor.traj);

    const validSpin = !isNil(precursor.bs?.wx) && !isNil(precursor.bs?.wz);

    // type assertion is safe here due isNil checks - React server just has a hard time with this type of narrowing
    const trueSpin = validSpin
      ? Math.sqrt(
          Math.pow(precursor.bs?.wx as number, 2) +
            Math.pow(precursor.bs?.wz as number, 2)
        )
      : undefined;

    const speedMPH = TrajHelper.getSpeedFromTraj(precursor.traj);
    const verticalAngleRad = Math.asin(
      (precursor.traj.vz * FTPS_TO_MPH) / speedMPH
    );
    const horizontalAngleRad = Math.asin(
      (precursor.traj.vx * FTPS_TO_MPH) /
        (-1 * speedMPH * Math.cos(verticalAngleRad))
    );

    const hitter = fire.data.hitterExt;
    const szDef = hitter?.height_ft
      ? PlateHelper.getDynamicStrikeZone(szType, hitter.height_ft)
      : VISIBLE_STRIKEZONE;

    const output: Partial<ITrackingData> = {
      // todo: replace twith confidence metrics once available for vision
      Validated: precursor.traj.ay > 0 && precursor.traj.ay < 50 ? 'Y' : 'N',

      // negative multiplier in gyro deg to match Rapsodo convention
      PITCH_GyroDegree:
        !isNil(trueSpin) && !isNil(precursor.bs?.wy)
          ? // type assertion is safe here due isNil checks - React server just has a hard time with this type of narrowing
            -1 * Math.atan2(precursor.bs?.wy as number, trueSpin) * RAD_TO_DEG
          : undefined,
      PITCH_HBSpin: undefined,
      PITCH_HBTrajectory: -1 * breaks.xInches,
      PITCH_HorizontalAngle: convertUnits(RAD_TO_DEG, horizontalAngleRad),
      PITCH_PlayerID: undefined,
      PITCH_ReleaseExtension: MOUND_DISTANCE_FT - precursor.traj.py,
      PITCH_ReleaseHeight: precursor.traj.pz,
      PITCH_ReleaseSide: -1 * precursor.traj.px,
      PITCH_ReleaseTime: 0,
      PITCH_Speed: speedMPH,
      PITCH_SpinAxis: undefined,

      // local spin (same as Rapsodo)
      PITCH_SpinX: precursor.bs?.wx,
      PITCH_SpinY: precursor.bs?.wy,
      PITCH_SpinZ: precursor.bs?.wz,

      PITCH_SpinEfficiency:
        !isNil(trueSpin) && !isNil(precursor.bs?.wnet)
          ? // type assertion is safe here due isNil checks - React server just has a hard time with this type of narrowing
            ((trueSpin as number) / (precursor.bs?.wnet as number)) * 100
          : undefined,
      PITCH_Strike: PlateHelper.isStrike({
        plateFt,
        szDef,
      }),
      PITCH_StrikeZoneHeight: convertUnits(FT_TO_INCHES, plateFt.plate_z),
      PITCH_StrikeZoneSide: convertUnits(FT_TO_INCHES, -1 * plateFt.plate_x),
      PITCH_StrikeZoneTime: flightTime,
      PITCH_TotalSpin: precursor.bs?.wnet,
      PITCH_TrueSpin: trueSpin,
      PITCH_VBSpin: undefined,
      PITCH_VBTrajectory: breaks.zInches,
      PITCH_VerticalAngle: convertUnits(RAD_TO_DEG, verticalAngleRad),
      PITCH_ZoneTime: flightTime,

      HIT_Distance: precursor.hit?.hit?.distanceFT ?? undefined,
      HIT_LaunchAngle: precursor.hit?.hit?.vLaunchDEG ?? undefined,
      HIT_ExitSpeed: precursor.hit?.hit?.exitMPH ?? undefined,
    };

    return output;
  }

  static getHawkeyeSpin = (data: IHawkeye, traj: ITrajectory): ISpin => {
    // hawkeye frame of ref is global => convert to local
    const mx = getLocalizeMatrix(new Vector3(traj.vx, traj.vy, traj.vz));

    const globalSpin = new Vector3(data.spinX, data.spinY, data.spinZ);
    const localSpin = globalSpin.applyMatrix3(mx);

    return {
      wx: localSpin.x,
      wy: localSpin.y,
      wz: localSpin.z,
    };
  };
}
